import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn, hash } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { modifier } from "ember-modifier";
import DButton from "discourse/components/d-button";
import FastEdit from "discourse/components/fast-edit";
import FastEditModal from "discourse/components/modal/fast-edit";
import PluginOutlet from "discourse/components/plugin-outlet";
import concatClass from "discourse/helpers/concat-class";
import { ajax } from "discourse/lib/ajax";
import { getAbsoluteURL } from "discourse/lib/get-url";
import Sharing from "discourse/lib/sharing";
import { clipboardCopy, postUrl, setCaretPosition } from "discourse/lib/utilities";
import { i18n } from "discourse-i18n";
import { setComponentTemplate } from "@ember/component";
import { createTemplateFactory } from "@ember/template-factory";
export function fixQuotes(str) {
  // u+201c, u+201d = “ ”
  // u+2018, u+2019 = ‘ ’
  return str.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'");
}
export default class PostTextSelectionToolbar extends Component {
  static #_ = (() => dt7948.g(this.prototype, "currentUser", [service]))();
  #currentUser = (() => (dt7948.i(this, "currentUser"), void 0))();
  static #_2 = (() => dt7948.g(this.prototype, "modal", [service]))();
  #modal = (() => (dt7948.i(this, "modal"), void 0))();
  static #_3 = (() => dt7948.g(this.prototype, "site", [service]))();
  #site = (() => (dt7948.i(this, "site"), void 0))();
  static #_4 = (() => dt7948.g(this.prototype, "siteSettings", [service]))();
  #siteSettings = (() => (dt7948.i(this, "siteSettings"), void 0))();
  static #_5 = (() => dt7948.g(this.prototype, "appEvents", [service]))();
  #appEvents = (() => (dt7948.i(this, "appEvents"), void 0))();
  static #_6 = (() => dt7948.g(this.prototype, "toasts", [service]))();
  #toasts = (() => (dt7948.i(this, "toasts"), void 0))();
  static #_7 = (() => dt7948.g(this.prototype, "isFastEditing", [tracked], function () {
    return false;
  }))();
  #isFastEditing = (() => (dt7948.i(this, "isFastEditing"), void 0))();
  appEventsListeners = (() => modifier(() => {
    this.appEvents.on("quote-button:edit", this, "toggleFastEdit");
    return () => {
      this.appEvents.off("quote-button:edit", this, "toggleFastEdit");
    };
  }))();
  get topic() {
    return this.args.data.topic;
  }
  get quoteState() {
    return this.args.data.quoteState;
  }
  get post() {
    return this.topic.postStream.findLoadedPost(this.args.data.quoteState.postId);
  }
  get quoteSharingEnabled() {
    return this.site.desktopView && this.quoteSharingSources.length > 0 && !this.topic.invisible && !this.topic.category?.read_restricted && (this.siteSettings.share_quote_visibility === "all" || this.siteSettings.share_quote_visibility === "anonymous" && !this.currentUser);
  }
  get quoteSharingSources() {
    return Sharing.activeSources(this.siteSettings.share_quote_buttons, this.siteSettings.login_required || this.topic.isPrivateMessage);
  }
  get quoteSharingShowLabel() {
    return this.quoteSharingSources.length > 1;
  }
  get shareUrl() {
    return getAbsoluteURL(postUrl(this.topic.slug, this.topic.id, this.post.post_number));
  }
  get embedQuoteButton() {
    const canCreatePost = this.topic.details.can_create_post;
    const canReplyAsNewTopic = this.topic.details.can_reply_as_new_topic;
    return (canCreatePost || canReplyAsNewTopic) && this.currentUser?.get("user_option.enable_quoting");
  }
  trapEvents(event) {
    event.stopPropagation();
  }
  static #_8 = (() => dt7948.n(this.prototype, "trapEvents", [action]))();
  async copyQuoteToClipboard() {
    const text = await this.args.data.buildQuote();
    clipboardCopy(text);
    this.toasts.success({
      duration: 3000,
      data: {
        message: i18n("post.quote_copied_to_clibboard")
      }
    });
    await this.args.data.hideToolbar();
  }
  static #_9 = (() => dt7948.n(this.prototype, "copyQuoteToClipboard", [action]))();
  async closeFastEdit() {
    this.isFastEditing = false;
    await this.args.data.hideToolbar();
  }
  static #_10 = (() => dt7948.n(this.prototype, "closeFastEdit", [action]))();
  async toggleFastEdit() {
    if (this.args.data.supportsFastEdit) {
      if (this.site.desktopView) {
        this.isFastEditing = !this.isFastEditing;
      } else {
        this.modal.show(FastEditModal, {
          model: {
            initialValue: this.args.data.quoteState.buffer,
            post: this.post
          }
        });
        this.args.data.hideToolbar();
      }
    } else {
      const result = await ajax(`/posts/${this.post.id}`);
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let bestIndex = 0;
      const rows = result.raw.split("\n");
      // selecting even a part of the text of a list item will include
      // "* " at the beginning of the buffer, we remove it to be able
      // to find it in row
      const buffer = fixQuotes(this.args.data.quoteState.buffer.split("\n")[0].replace(/^\* /, ""));
      rows.some((row, index) => {
        if (row.length && row.includes(buffer)) {
          bestIndex = index;
          return true;
        }
      });
      this.args.data.editPost(this.post);
      document.querySelector("#reply-control")?.addEventListener("transitionend", () => {
        const textarea = document.querySelector(".d-editor-input");
        if (!textarea || this.isDestroyed || this.isDestroying) {
          return;
        }
        // best index brings us to one row before as slice start from 1
        // we add 1 to be at the beginning of next line, unless we start from top
        setCaretPosition(textarea, rows.slice(0, bestIndex).join("\n").length + (bestIndex > 0 ? 1 : 0));
        // ensures we correctly scroll to caret and reloads composer
        // if we do another selection/edit
        textarea.blur();
        textarea.focus();
      });
      this.args.data.hideToolbar();
      return;
    }
  }
  static #_11 = (() => dt7948.n(this.prototype, "toggleFastEdit", [action]))();
  share(source) {
    Sharing.shareSource(source, {
      url: this.shareUrl,
      title: this.topic.title,
      quote: window.getSelection().toString()
    });
  }
  static #_12 = (() => dt7948.n(this.prototype, "share", [action]))();
  static #_13 = (() => setComponentTemplate(createTemplateFactory(
  /*
    
      {{! template-lint-disable no-invalid-interactive }}
      {{! template-lint-disable no-pointer-down-event-binding }}
      <div
        {{on "mousedown" this.trapEvents}}
        {{on "mouseup" this.trapEvents}}
        class={{concatClass
          "quote-button"
          "visible"
          (if this.isFastEditing "fast-editing")
        }}
        {{this.appEventsListeners}}
      >
        <div class="buttons">
          <PluginOutlet
            @name="post-text-buttons"
            @defaultGlimmer={{true}}
            @outletArgs={{hash data=@data post=this.post}}
          >
            {{#if this.embedQuoteButton}}
              <DButton
                @icon="quote-left"
                @label="post.quote_reply"
                @title="post.quote_reply_shortcut"
                class="btn-flat insert-quote"
                @action={{@data.insertQuote}}
              />
            {{/if}}
  
            {{#if @data.canEditPost}}
              <DButton
                @icon="pencil"
                @label="post.quote_edit"
                @title="post.quote_edit_shortcut"
                class="btn-flat quote-edit-label"
                {{on "click" this.toggleFastEdit}}
              />
            {{/if}}
  
            {{#if @data.canCopyQuote}}
              <DButton
                @icon="copy"
                @label="post.quote_copy"
                @title="post.quote_copy"
                class="btn-flat copy-quote"
                {{on "click" this.copyQuoteToClipboard}}
              />
            {{/if}}
  
            <PluginOutlet
              @name="quote-share-buttons-before"
              @connectorTagName="span"
              @outletArgs={{hash data=@data}}
            />
  
            {{#if this.quoteSharingEnabled}}
              <span class="quote-sharing">
                {{#if this.quoteSharingShowLabel}}
                  <DButton
                    @icon="share"
                    @label="post.quote_share"
                    class="btn-flat quote-share-label"
                  />
                {{/if}}
  
                <span class="quote-share-buttons">
                  {{#each this.quoteSharingSources as |source|}}
                    <DButton
                      @action={{fn this.share source}}
                      @translatedTitle={{source.title}}
                      @icon={{source.icon}}
                      class="btn-flat"
                    />
                  {{/each}}
  
                  <PluginOutlet
                    @name="quote-share-buttons-after"
                    @connectorTagName="span"
                    @outletArgs={{hash data=@data}}
                  />
                </span>
              </span>
            {{/if}}
          </PluginOutlet>
        </div>
  
        <div class="extra">
          {{#if this.isFastEditing}}
            <FastEdit
              @initialValue={{@data.quoteState.buffer}}
              @post={{this.post}}
              @close={{this.closeFastEdit}}
            />
          {{/if}}
  
          <PluginOutlet @name="quote-button-after" @connectorTagName="div" />
        </div>
      </div>
    
  */
  {
    "id": "h0zhSUG+",
    "block": "[[[1,\"\\n\"],[1,\"    \"],[11,0],[16,0,[28,[32,0],[\"quote-button\",\"visible\",[52,[30,0,[\"isFastEditing\"]],\"fast-editing\"]],null]],[4,[32,1],[\"mousedown\",[30,0,[\"trapEvents\"]]],null],[4,[32,1],[\"mouseup\",[30,0,[\"trapEvents\"]]],null],[4,[30,0,[\"appEventsListeners\"]],null,null],[12],[1,\"\\n      \"],[10,0],[14,0,\"buttons\"],[12],[1,\"\\n        \"],[8,[32,2],null,[[\"@name\",\"@defaultGlimmer\",\"@outletArgs\"],[\"post-text-buttons\",true,[28,[32,3],null,[[\"data\",\"post\"],[[30,1],[30,0,[\"post\"]]]]]]],[[\"default\"],[[[[1,\"\\n\"],[41,[30,0,[\"embedQuoteButton\"]],[[[1,\"            \"],[8,[32,4],[[24,0,\"btn-flat insert-quote\"]],[[\"@icon\",\"@label\",\"@title\",\"@action\"],[\"quote-left\",\"post.quote_reply\",\"post.quote_reply_shortcut\",[30,1,[\"insertQuote\"]]]],null],[1,\"\\n\"]],[]],null],[1,\"\\n\"],[41,[30,1,[\"canEditPost\"]],[[[1,\"            \"],[8,[32,4],[[24,0,\"btn-flat quote-edit-label\"],[4,[32,1],[\"click\",[30,0,[\"toggleFastEdit\"]]],null]],[[\"@icon\",\"@label\",\"@title\"],[\"pencil\",\"post.quote_edit\",\"post.quote_edit_shortcut\"]],null],[1,\"\\n\"]],[]],null],[1,\"\\n\"],[41,[30,1,[\"canCopyQuote\"]],[[[1,\"            \"],[8,[32,4],[[24,0,\"btn-flat copy-quote\"],[4,[32,1],[\"click\",[30,0,[\"copyQuoteToClipboard\"]]],null]],[[\"@icon\",\"@label\",\"@title\"],[\"copy\",\"post.quote_copy\",\"post.quote_copy\"]],null],[1,\"\\n\"]],[]],null],[1,\"\\n          \"],[8,[32,2],null,[[\"@name\",\"@connectorTagName\",\"@outletArgs\"],[\"quote-share-buttons-before\",\"span\",[28,[32,3],null,[[\"data\"],[[30,1]]]]]],null],[1,\"\\n\\n\"],[41,[30,0,[\"quoteSharingEnabled\"]],[[[1,\"            \"],[10,1],[14,0,\"quote-sharing\"],[12],[1,\"\\n\"],[41,[30,0,[\"quoteSharingShowLabel\"]],[[[1,\"                \"],[8,[32,4],[[24,0,\"btn-flat quote-share-label\"]],[[\"@icon\",\"@label\"],[\"share\",\"post.quote_share\"]],null],[1,\"\\n\"]],[]],null],[1,\"\\n              \"],[10,1],[14,0,\"quote-share-buttons\"],[12],[1,\"\\n\"],[42,[28,[31,2],[[28,[31,2],[[30,0,[\"quoteSharingSources\"]]],null]],null],null,[[[1,\"                  \"],[8,[32,4],[[24,0,\"btn-flat\"]],[[\"@action\",\"@translatedTitle\",\"@icon\"],[[28,[32,5],[[30,0,[\"share\"]],[30,2]],null],[30,2,[\"title\"]],[30,2,[\"icon\"]]]],null],[1,\"\\n\"]],[2]],null],[1,\"\\n                \"],[8,[32,2],null,[[\"@name\",\"@connectorTagName\",\"@outletArgs\"],[\"quote-share-buttons-after\",\"span\",[28,[32,3],null,[[\"data\"],[[30,1]]]]]],null],[1,\"\\n              \"],[13],[1,\"\\n            \"],[13],[1,\"\\n\"]],[]],null],[1,\"        \"]],[]]]]],[1,\"\\n      \"],[13],[1,\"\\n\\n      \"],[10,0],[14,0,\"extra\"],[12],[1,\"\\n\"],[41,[30,0,[\"isFastEditing\"]],[[[1,\"          \"],[8,[32,6],null,[[\"@initialValue\",\"@post\",\"@close\"],[[30,1,[\"quoteState\",\"buffer\"]],[30,0,[\"post\"]],[30,0,[\"closeFastEdit\"]]]],null],[1,\"\\n\"]],[]],null],[1,\"\\n        \"],[8,[32,2],null,[[\"@name\",\"@connectorTagName\"],[\"quote-button-after\",\"div\"]],null],[1,\"\\n      \"],[13],[1,\"\\n    \"],[13],[1,\"\\n  \"]],[\"@data\",\"source\"],false,[\"if\",\"each\",\"-track-array\"]]",
    "moduleName": "/var/www/discourse/app/assets/javascripts/discourse/node_modules/.embroider/rewritten-app/components/post-text-selection-toolbar.js",
    "scope": () => [concatClass, on, PluginOutlet, hash, DButton, fn, FastEdit],
    "isStrictMode": true
  }), this))();
}